import { IJobFilter } from 'common/types/job.types'
import { INPUT_PLACEHOLDER } from 'common/types/message.types'
import { useJobsFeed } from 'hooks/use-jobs-feed/useJobsFeed'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import ContentEditable from 'react-contenteditable'
import styled, { useTheme } from 'styled-components'
import { JobSearchInputContainer } from './styles'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { Search } from '@material-ui/icons'
import { Box } from '@material-ui/core'

const StyledInputTextField = styled(ContentEditable)<{
  singleLine?: boolean
  width?: string
  scrollBarColor?: string
  searchText?: string
}>`
  && {
    max-height: 140px;
    width: ${props => (props?.width ? props.width : '80%')};
    outline: none;
    overflow-y: auto;
    color: ${props => props.theme.colors.accent1};
    position: relative;
    cursor: text;
    min-height: 20px;
    font-size: 1rem;
  }

  :focus {
    outline: none !important;
  }

  ${props =>
    props.singleLine
      ? `
      white-space: nowrap;
      overflow-x: hidden;
    `
      : ``}

  ::-webkit-scrollbar {
    width: 0.43em;
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 0.3125em;
    background-color: ${props => (props?.scrollBarColor ? props.scrollBarColor : props.theme.colors.accent)};
    outline: 1px solid transparent;
  }

  ::-webkit-scrollbar:horizontal {
    display: none;
  }

  ::-webkit-scrollbar-corner {
    color: transparent;
  }

  ::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '${props => (!!props.searchText?.length ? '' : props.placeholder ?? INPUT_PLACEHOLDER)}';
    color: ${props => props.theme.colors.accent};
    font-size: 1rem;
    width: 100%;
    height: 100%;
    pointer-events: none;
    padding-left: 0.875rem;
  }

  svg.hashtag-icon path,
  a {
    color: ${props => props.theme.colors.primary};
    fill: ${props => props.theme.colors.primary} !important;
  }

  svg {
    width: calc(0.5625rem);
    height: calc(1rem);
    transform: translateY(25%);
  }
`
interface IJobSearchInput {
  filter: IJobFilter
  setFilter: Dispatch<SetStateAction<IJobFilter>>
}

const useInputStyles = (props: { fullWidth?: boolean; height: string; singleLine?: boolean }) => {
  const { fullWidth, height, singleLine } = props

  return useMemo(
    () => ({
      textfield: {
        width: fullWidth ? '100% !important' : null,
        height,
        border: 'none',
        overflow: singleLine ? 'visible' : 'auto',
        overflowX: singleLine ? 'hidden' : 'unset',
        paddingLeft: '0.875rem'
      }
    }),
    [props]
  )
}

const SearchInput = ({ filter, setFilter }: IJobSearchInput) => {
  const theme = useTheme()
  const [searchCriteria, setSearchCriteria] = useState('')

  const styles = useInputStyles({
    fullWidth: true,
    height: '',
    singleLine: true
  })

  const { fetchJobs, noOfSearchResults } = useJobsFeed()
  const { backendPartner, backendUser } = useAuth()
  const { logEventAnalytics } = useAnalytics()

  const trackJobSearchEvent = () => {
    const partnerLog = {}
    const userLog = {}

    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendUser?.partnerId
      partnerLog['Company Name'] = backendPartner?.name
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Job search made',
      'mixpanel',
      'Job search made',
      {
        'User ID': backendUser?.id,
        'Profile search query': searchCriteria,
        'Profile search results': noOfSearchResults,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
        ...userLog,
        ...partnerLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilter(prev => {
        const payload = {
          ...prev,
          titleAndCompany: searchCriteria.trim()
        }
        return payload
      })
      fetchJobs()
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchCriteria])

  return (
    <JobSearchInputContainer>
      <StyledInputTextField
        html={searchCriteria}
        style={styles.textfield}
        width={'100%'}
        disabled={false}
        singleLine={true}
        onChange={evt => {
          setSearchCriteria(evt?.currentTarget?.innerText)
          trackJobSearchEvent()
        }}
        placeholder="Search by job title..."
        searchText={searchCriteria}
      />
      <Box color={theme.colors.accent} display="flex" justifyContent="center" alignItems="center">
        <Search />
      </Box>
    </JobSearchInputContainer>
  )
}

export default SearchInput
